import React, { useEffect, useRef, useState, useCallback } from "react";

const COLORS = ["#00ff00", "#00cc00", "#33ff33", "#008000", "#ccffcc"]; // Matrix Greens
const CHARS = "0123456789ABCDEF@#$%&";
const MAX_TRAIL_LENGTH = 20;

export const CyberCursor = ({ disabled = false }) => {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [trail, setTrail] = useState([]);
  const [isHovering, setIsHovering] = useState(false);
  const cursorRef = useRef(null);
  
  // Track mouse movement
  useEffect(() => {
    const handleMouseMove = (e) => {
      setPosition({ x: e.clientX, y: e.clientY });
      
      // Check if hovering over clickable elements (always check, even when disabled)
      const target = e.target;
      const isClickable = 
        target.tagName === 'BUTTON' || 
        target.tagName === 'A' || 
        target.closest('button') || 
        target.closest('a') ||
        target.classList.contains('cursor-pointer') ||
        target.closest('.cursor-pointer');
      
      setIsHovering(!!isClickable);
      
      // Don't create trail when disabled (modal is open)
      if (disabled) return;

      // Add trail point
      const newChar = CHARS[Math.floor(Math.random() * CHARS.length)];
      const newPoint = { 
        x: e.clientX, 
        y: e.clientY, 
        id: Date.now() + Math.random(), 
        char: newChar,
        age: 0 
      };

      setTrail(prev => {
        const next = [...prev, newPoint];
        if (next.length > MAX_TRAIL_LENGTH) return next.slice(next.length - MAX_TRAIL_LENGTH);
        return next;
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [disabled]);

  // Animate trail fading
  useEffect(() => {
    const interval = setInterval(() => {
      setTrail(prev => 
        prev
          .map(p => ({ ...p, age: p.age + 1 }))
          .filter(p => p.age < 15) // Remove old points
      );
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <style>{`
        body { cursor: none; } /* Hide default cursor */
        .cyber-cursor-trail { pointer-events: none; position: fixed; z-index: 9998; font-family: monospace; font-weight: bold; }
        .cyber-cursor-main { pointer-events: none; position: fixed; z-index: 9999; transform: translate(-50%, -50%); transition: width 0.2s, height 0.2s; mix-blend-mode: difference; }
      `}</style>

      {/* Trail */}
      {trail.map((point, i) => (
        <div 
          key={point.id}
          className="cyber-cursor-trail"
          style={{
            left: point.x,
            top: point.y,
            fontSize: `${Math.max(8, 20 - point.age * 2)}px`,
            opacity: 1 - point.age / 15,
            color: COLORS[i % COLORS.length],
            textShadow: '0 0 5px #00ff00',
            transform: `translate(-50%, -50%) rotate(${point.age * 10}deg)`
          }}
        >
          {point.char}
        </div>
      ))}

      {/* Main Cursor (Crosshair/Box) */}
      <div 
        className={`cyber-cursor-main flex items-center justify-center ${isHovering ? "w-12 h-12" : "w-6 h-6"}`}
        style={{ left: position.x, top: position.y }}
      >
        <div className={`absolute border border-green-500 rounded-full transition-all duration-300 ${isHovering ? "w-full h-full border-2 border-dashed animate-spin-slow" : "w-2 h-2 bg-white"}`}></div>
        {!isHovering && <div className="absolute w-full h-[1px] bg-green-500/50"></div>}
        {!isHovering && <div className="absolute h-full w-[1px] bg-green-500/50"></div>}
      </div>
    </>
  );
};
